import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';

/**
 * App-wide banner shown whenever the active tenant is missing a HubSpot or
 * Stripe connection. Rendered in the Layout so it appears on every page until
 * the connections are configured. The call to action deep-links straight to
 * the Connections tab of the Settings page.
 */
const ConnectionBanner: React.FC = () => {
  const { missingHubspot, missingStripe, hasMissingConnection } = useConnectionStatus();

  if (!hasMissingConnection) return null;

  const both = missingHubspot && missingStripe;
  const service = both ? 'HubSpot and Stripe' : missingHubspot ? 'HubSpot' : 'Stripe';
  const pronoun = both ? 'them' : 'it';

  return (
    <Alert className="rounded-none border-x-0 border-t-0 border-amber-300 bg-amber-50 text-amber-800">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="flex flex-col gap-2 text-amber-800 sm:flex-row sm:items-center sm:justify-between">
        <span>
          <span className="font-medium">
            {service} {both ? 'connections are' : 'connection is'} not set up.
          </span>{' '}
          Connect {pronoun} to enable syncing between HubSpot and Stripe.
        </span>
        <Button asChild variant="outline" size="sm" className="self-start sm:self-auto">
          <Link to="/settings?tab=connections">
            Set up connections
            <ArrowRight />
          </Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
};

export default ConnectionBanner;
